import { RefreshCw, AlertTriangle } from 'lucide-react';
import { OverdueReviewWidget } from './OverdueReviewWidget';
import { UnaddressedAlertsWidget } from './UnaddressedAlertsWidget';
import { ExpiringContractsWidget } from './ExpiringContractsWidget';
import { SoftFlagSummaryWidget } from './SoftFlagSummaryWidget';
import { usePortfolioRisk, useRefreshPortfolioRisk } from '@/hooks/usePortfolioRisk';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRefreshed(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Portfolio Risk tab — FR-086.
 *
 * Four read-only widgets:
 *   1. Overdue Review Items (red) — jobs pending review > 48h
 *   2. Unaddressed Alerts (amber) — APPROVED_WITH_ALERTS without annotation
 *   3. Expiring Contracts (orange) — contracts expiring within 30 days
 *   4. Soft-Flag Summary (blue) — aggregated soft flag counts per rule key
 *
 * All data scoped to client_id via RLS. No actions — read-only view.
 * Visible to all roles (admin sees full org view, reviewers/viewers same client scope).
 *
 * WCAG 2.1 AA: aria-live on count badges, section landmarks, descriptive aria-labels.
 */
export function PortfolioRiskTab() {
  const { data, isLoading, error, refetch } = usePortfolioRisk();
  const refreshPortfolioRisk = useRefreshPortfolioRisk();

  function handleRefresh() {
    void refreshPortfolioRisk();
    void refetch();
  }

  return (
    <div className="space-y-4">
      {/* Header row: title + last-refreshed + manual refresh button */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-foreground">Portfolio Risk</h2>
        <div className="flex items-center gap-3">
          {data && (
            <span className="text-xs text-muted-foreground">
              Last refreshed {formatRefreshed(data.refreshed_at)}
            </span>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoading}
            aria-label="Refresh portfolio risk data"
            className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium border border-border bg-background hover:bg-muted/40 disabled:opacity-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load portfolio risk data.</span>
          <button type="button" onClick={() => void refetch()} className="underline hover:no-underline ml-2">
            Retry
          </button>
        </div>
      )}

      {/* 2×2 responsive widget grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <OverdueReviewWidget
          count={data?.overdue_review_count ?? 0}
          items={data?.overdue_review_items ?? []}
          loading={isLoading}
        />
        <UnaddressedAlertsWidget
          count={data?.unaddressed_alerts_count ?? 0}
          items={data?.unaddressed_alerts_items ?? []}
          loading={isLoading}
        />
        <ExpiringContractsWidget
          count={data?.expiring_contracts_count ?? 0}
          items={data?.expiring_contracts_items ?? []}
          loading={isLoading}
        />
        <SoftFlagSummaryWidget
          rows={data?.soft_flag_rows ?? []}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
